import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Form,
  Modal,
  Spinner,
  Table,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import PageContentLayout from '@/components/layout/PageContentLayout/PageContentLayout.js';
import { fetchJianyingJobs } from '@/features/automation/jianying/services/jianyingJobService.js';
import {
  fetchJianyingTemplateAvailability,
  fetchJianyingTemplates,
} from '../../services/jianyingTemplateService.js';
import {
  fetchJianyingDevices,
  updateJianyingDevice,
} from '../../services/jianyingDeviceService.js';
import {
  buildJianyingTemplateCoverage,
  filterAndSortJianyingDevices,
  getJianyingDeviceSchedulingState,
  summarizeJianyingDevices,
} from '../../utils/jianyingDevices.js';
import '../../../../jianying/JianyingCommon.scss';
import './JianyingDevicesPage.scss';

const EMPTY_DEVICE = Object.freeze({
  id: '',
  name: '',
  platform: 'Windows',
  priority: 100,
  max_pending_jobs: 10,
});

const errorMessage = (error, fallback) =>
  error.response?.data?.message || error.message || fallback;

const formatHeartbeat = (value) => {
  if (!value) return '尚未上报';
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return '-';
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 60) return `${seconds} 秒前`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} 分钟前`;
  return new Date(value).toLocaleString('zh-CN', { hour12: false });
};

const deviceToForm = (device) => ({
  id: device.id,
  name: device.name || '',
  platform: device.platform || 'Windows',
  priority: Number(device.priority) || 100,
  max_pending_jobs: Number(device.max_pending_jobs) || 10,
});

const getTemplateCell = (state) => {
  if (state === 'Available') {
    return <span className="jianying-device-template-state is-available"><i className="bi bi-check-lg" /> 存在</span>;
  }
  if (state === 'Ignored') {
    return <span className="jianying-device-template-state is-ignored">— 不参与调度</span>;
  }
  return <span className="jianying-device-template-state is-unavailable"><i className="bi bi-x-lg" /> 不可用</span>;
};

const JianyingDevicesPage = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [availabilityItems, setAvailabilityItems] = useState([]);
  const [exportingCount, setExportingCount] = useState(0);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [editorMessage, setEditorMessage] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [editingDeviceId, setEditingDeviceId] = useState('');
  const [form, setForm] = useState(EMPTY_DEVICE);
  const requestIdRef = useRef(0);

  const loadData = useCallback(async ({ silent = false } = {}) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    if (!silent) {
      setLoading(true);
      setMessage('');
    }
    try {
      const [deviceData, templateData, exportingJobs] = await Promise.all([
        fetchJianyingDevices(),
        fetchJianyingTemplates({ status: 'Scanned' }),
        fetchJianyingJobs({ status: 'Exporting', page: 1, limit: 100 }),
      ]);
      const activeTemplates = templateData.filter(
        (template) => template.is_active !== false && template.is_deleted !== true,
      );
      const nextAvailability = activeTemplates.length > 0
        ? await fetchJianyingTemplateAvailability(
          activeTemplates.map((template) => template.id),
        )
        : [];
      if (requestIdRef.current !== requestId) return;
      setDevices(deviceData);
      setTemplates(activeTemplates);
      setAvailabilityItems(nextAvailability);
      setExportingCount(exportingJobs.total);
    } catch (error) {
      if (!silent && requestIdRef.current === requestId) {
        setMessage(errorMessage(error, '读取剪映设备失败'));
      }
    } finally {
      if (!silent && requestIdRef.current === requestId) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const timer = window.setInterval(
      () => loadData({ silent: true }),
      10000,
    );
    return () => {
      requestIdRef.current += 1;
      window.clearInterval(timer);
    };
  }, [loadData]);

  const visibleDevices = useMemo(
    () => filterAndSortJianyingDevices(devices, { filter, query }),
    [devices, filter, query],
  );
  const summary = useMemo(
    () => summarizeJianyingDevices(devices, exportingCount),
    [devices, exportingCount],
  );
  const coverage = useMemo(
    () => buildJianyingTemplateCoverage({
      templates,
      devices,
      availabilityItems,
    }),
    [availabilityItems, devices, templates],
  );
  const orderedDevices = useMemo(
    () => filterAndSortJianyingDevices(devices),
    [devices],
  );
  const availableTemplateCount = coverage.filter((item) => item.available).length;

  const openEdit = (device) => {
    setEditingDeviceId(device.id);
    setForm(deviceToForm(device));
    setEditorMessage('');
    setShowEditor(true);
  };

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const saveDevice = async () => {
    const normalized = {
      id: String(form.id || '').trim(),
      name: String(form.name || '').trim(),
      platform: String(form.platform || '').trim(),
      priority: Number(form.priority),
      max_pending_jobs: Number(form.max_pending_jobs),
    };
    if (!normalized.name || !normalized.platform || !normalized.id) {
      setEditorMessage('请填写设备名称、设备 ID 和平台。');
      return;
    }
    setSaving(true);
    setEditorMessage('');
    try {
      await updateJianyingDevice(editingDeviceId, {
        name: normalized.name,
        platform: normalized.platform,
        priority: normalized.priority,
        max_pending_jobs: normalized.max_pending_jobs,
      });
      setShowEditor(false);
      await loadData();
    } catch (error) {
      setEditorMessage(errorMessage(error, '保存设备配置失败'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContentLayout
      pageClassName="jianying-workspace jianying-devices-page"
      title="剪映设备"
      description="查看 Python 服务的可调度状态，维护优先级、积压上限和模板覆盖。"
      actions={(
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            onClick={() => navigate('/automation/jianying/list')}
          >
            <i className="bi bi-list-task" /> 查看剪映任务
          </Button>
        </div>
      )}
    >
      {message ? <p className="jianying-notice mb-3" role="alert">{message}</p> : null}

      <section className="jianying-device-readiness" aria-label="设备调度状态">
        <div className={`jianying-device-readiness__main ${summary.ready > 0 ? 'is-ready' : 'is-unavailable'}`}>
          <span className="jianying-device-readiness__signal" aria-hidden="true" />
          <div>
            <strong>{summary.ready > 0 ? '调度可用' : '当前不可调度'}</strong>
            <span>
              {summary.ready > 0
                ? `有 ${summary.ready} 台设备可立即领取任务`
                : '没有心跳正常且桌面可用的设备'}
            </span>
          </div>
        </div>
        <div className="jianying-device-readiness__metric">
          <span>可调度设备</span><strong>{summary.ready}</strong><small>心跳和桌面正常</small>
        </div>
        <div className="jianying-device-readiness__metric">
          <span>需要处理</span><strong>{summary.attention}</strong><small>锁屏、超时或异常</small>
        </div>
        <div className="jianying-device-readiness__metric">
          <span>正在导出</span><strong>{summary.exporting}</strong><small>每台最多一个</small>
        </div>
      </section>

      <section className="jianying-device-panel">
        <header className="jianying-device-panel__head">
          <div>
            <h3>设备列表</h3>
            <p>设备由 Python 服务首次心跳自动注册；优先级数字越小越优先。</p>
          </div>
          <div className="jianying-device-toolbar">
            <Form.Control
              aria-label="搜索设备"
              placeholder="搜索设备名称或 ID"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            <div className="jianying-device-filter" aria-label="设备状态筛选">
              {[
                ['all', '全部'],
                ['ready', '可调度'],
                ['attention', '需处理'],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={filter === value ? 'is-active' : ''}
                  onClick={() => setFilter(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </header>
        <div className="jianying-table-wrap">
          <Table hover responsive={false} className="jianying-device-table">
            <thead className="table-light">
              <tr>
                <th>设备</th><th>状态</th><th>优先级</th><th>任务积压</th>
                <th>可用模板</th><th>最近心跳</th><th className="text-end">操作</th>
              </tr>
            </thead>
            <tbody>
              {visibleDevices.map((device) => {
                const state = getJianyingDeviceSchedulingState(device);
                const pending = Number(device.pending_job_count) || 0;
                const capacity = Number(device.max_pending_jobs) || 0;
                return (
                  <tr key={device.id}>
                    <td>
                      <div className="jianying-device-identity">
                        <span className="jianying-device-identity__icon">
                          {device.platform === 'macOS' ? 'MAC' : 'WIN'}
                        </span>
                        <div><strong>{device.name}</strong><span>{device.id}</span></div>
                      </div>
                    </td>
                    <td>
                      <span className={`jianying-device-state is-${state.tone}`}>
                        {state.label}
                      </span>
                    </td>
                    <td>{device.priority}</td>
                    <td>
                      {pending} / {capacity}
                      <small className="jianying-device-cell-note">
                        {pending >= capacity ? '已达上限' : `还可分配 ${capacity - pending}`}
                      </small>
                    </td>
                    <td>{Number(device.available_template_count) || 0} / {templates.length}</td>
                    <td>{formatHeartbeat(device.last_heartbeat_at)}</td>
                    <td className="text-end">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => openEdit(device)}
                      >
                        <i className="bi bi-pencil" /> 编辑
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {!loading && visibleDevices.length === 0 ? (
                <tr>
                  <td colSpan="7" className="jianying-empty">
                    {devices.length === 0
                      ? '尚无已注册设备。请先运行 setup.cmd，配置数据库并启动 Python 服务。'
                      : '没有符合条件的设备'}
                  </td>
                </tr>
              ) : null}
              {loading ? (
                <tr><td colSpan="7" className="jianying-empty"><Spinner size="sm" /> 正在读取设备</td></tr>
              ) : null}
            </tbody>
          </Table>
        </div>
        <footer className="jianying-device-rules">
          <strong>自动选择顺序</strong>
          <span>优先设备</span><i className="bi bi-chevron-right" />
          <span>优先级</span><i className="bi bi-chevron-right" />
          <span>当前积压</span><i className="bi bi-chevron-right" />
          <span>最久未分配</span>
          <small>锁屏和心跳超时设备不会领取新任务。</small>
        </footer>
      </section>

      <section className="jianying-device-panel">
        <header className="jianying-device-panel__head">
          <div>
            <h3>模板覆盖</h3>
            <p>模板只要存在于任意一台可调度设备，就可以创建任务。</p>
          </div>
          <span className={`jianying-device-coverage-summary ${availableTemplateCount === templates.length && templates.length > 0 ? 'is-complete' : ''}`}>
            {availableTemplateCount} / {templates.length} 个模板可用
          </span>
        </header>
        <div className="jianying-table-wrap">
          <Table responsive={false} className="jianying-device-coverage">
            <thead className="table-light">
              <tr>
                <th>模板</th>
                {orderedDevices.map((device) => <th key={device.id}>{device.name}</th>)}
                <th>整体结果</th>
              </tr>
            </thead>
            <tbody>
              {coverage.map((template) => (
                <tr key={template.id}>
                  <td><strong>{template.name}</strong><small>{template.draft_name}</small></td>
                  {orderedDevices.map((device) => (
                    <td key={device.id}>{getTemplateCell(template.deviceStates[device.id])}</td>
                  ))}
                  <td>
                    <span className={`jianying-device-state ${template.available ? 'is-ready' : 'is-danger'}`}>
                      {template.available ? '可创建任务' : '不可用'}
                    </span>
                  </td>
                </tr>
              ))}
              {!loading && coverage.length === 0 ? (
                <tr>
                  <td colSpan={orderedDevices.length + 2} className="jianying-empty">
                    暂无已扫描并启用的剪映模板
                  </td>
                </tr>
              ) : null}
            </tbody>
          </Table>
        </div>
        <footer className="jianying-device-coverage__foot">
          只有“可调度”设备参与整体结果；其他设备不参与本次模板可用性判断。
        </footer>
      </section>

      <Modal
        show={showEditor}
        onHide={() => !saving && setShowEditor(false)}
        centered
        className="jianying-device-editor"
      >
        <Modal.Header closeButton>
          <Modal.Title>编辑设备</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editorMessage ? (
            <p className="jianying-notice mb-3" role="alert">{editorMessage}</p>
          ) : null}
          <div className="jianying-device-editor__grid">
            <Form.Group className="is-wide" controlId="jianying-device-name">
              <Form.Label>设备名称</Form.Label>
              <Form.Control
                value={form.name}
                disabled={saving}
                onChange={(event) => setField('name', event.target.value)}
              />
            </Form.Group>
            <Form.Group className="is-wide" controlId="jianying-device-id">
              <Form.Label>设备 ID</Form.Label>
              <Form.Control
                value={form.id}
                disabled
              />
              <Form.Text>由 Python 服务自动注册，已被历史执行记录引用，不可修改。</Form.Text>
            </Form.Group>
            <Form.Group controlId="jianying-device-platform">
              <Form.Label>平台</Form.Label>
              <Form.Select
                value={form.platform}
                disabled={saving}
                onChange={(event) => setField('platform', event.target.value)}
              >
                <option value="Windows">Windows</option>
                <option value="macOS">macOS</option>
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="jianying-device-priority">
              <Form.Label>优先级</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={form.priority}
                disabled={saving}
                onChange={(event) => setField('priority', event.target.value)}
              />
              <Form.Text>数字越小越优先。</Form.Text>
            </Form.Group>
            <Form.Group className="is-wide" controlId="jianying-device-capacity">
              <Form.Label>最多积压任务</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={form.max_pending_jobs}
                disabled={saving}
                onChange={(event) => setField('max_pending_jobs', event.target.value)}
              />
              <Form.Text>这是未完成任务上限，不代表可以并行导出多个视频。</Form.Text>
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" disabled={saving} onClick={() => setShowEditor(false)}>
            取消
          </Button>
          <Button disabled={saving} onClick={saveDevice}>
            {saving ? <><Spinner size="sm" /> 保存中</> : '保存设备'}
          </Button>
        </Modal.Footer>
      </Modal>
    </PageContentLayout>
  );
};

export default JianyingDevicesPage;
